import { canUseFastOptimizationMode } from "../lib/accessControl.js";

export const DEFAULT_OPTIMIZATION_MODE = "fast";
export const FALLBACK_OPTIMIZATION_MODE = "standard";

export const OPTIMIZATION_MODE_OPTIONS = [
  {    key: "fast",
    label: "Rapide",
    description: "Réponse interactive la plus courte.",
    summary: "Itération courte",
    solverTimeoutSeconds: 5,
    executionStrategy: "interactive",
  },
  {
    key: "standard",
    label: "Standard",
    description: "Budget équilibré pour une recherche plus aboutie.",
    summary: "Équilibré",
    solverTimeoutSeconds: 10,
    executionStrategy: "interactive",
  },
  {
    key: "deep",
    label: "Approfondi",
    description: "Budget étendu, prêt pour des runs plus longs.",
    summary: "Budget étendu",
    solverTimeoutSeconds: 30,
    executionStrategy: "async_ready",
  },
];

export const OPTIMIZATION_MODE_OPTIONS_BY_KEY = new Map(
  OPTIMIZATION_MODE_OPTIONS.map((option) => [option.key, option]),
);

export function resolveOptimizationModeAvailability(resultsData = null, accessState = null) {
  if (typeof resultsData?.can_use_fast_optimization_mode === "boolean") {
    return {
      canUseFastMode: resultsData.can_use_fast_optimization_mode,
      fastModeEnabled: resultsData.fast_optimization_mode_enabled !== false,
    };
  }

  const canUseFastMode = canUseFastOptimizationMode(accessState);

  return {
    canUseFastMode,
    fastModeEnabled: canUseFastMode,
  };
}

export function normalizeOptimizationMode(value, fallback = DEFAULT_OPTIMIZATION_MODE) {
  const normalizedValue = typeof value === "string"
    ? value.trim().toLowerCase()
    : "";

  if (OPTIMIZATION_MODE_OPTIONS_BY_KEY.has(normalizedValue)) {
    return normalizedValue;
  }

  return fallback;
}

export function getOptimizationModeOption(value, fallback = DEFAULT_OPTIMIZATION_MODE) {
  return OPTIMIZATION_MODE_OPTIONS_BY_KEY.get(normalizeOptimizationMode(value, fallback))
    || OPTIMIZATION_MODE_OPTIONS_BY_KEY.get(DEFAULT_OPTIMIZATION_MODE)
    || OPTIMIZATION_MODE_OPTIONS[0];
}

export function resolveAvailableOptimizationModeOptions({
  canUseFastMode = false,
  fastModeEnabled = false,
} = {}) {
  const includeFastMode = canUseFastMode === true && fastModeEnabled !== false;
  return OPTIMIZATION_MODE_OPTIONS.filter((option) => includeFastMode || option.key !== "fast");
}

export function resolveEffectiveOptimizationMode(
  value,
  {
    canUseFastMode = false,
    fastModeEnabled = false,
    fallback = FALLBACK_OPTIMIZATION_MODE,
  } = {},
) {
  const availableOptions = resolveAvailableOptimizationModeOptions({ canUseFastMode, fastModeEnabled });
  const availableKeys = new Set(availableOptions.map((option) => option.key));
  const normalizedValue = normalizeOptimizationMode(value, "");
  if (normalizedValue && availableKeys.has(normalizedValue)) {
    return normalizedValue;
  }
  if (availableKeys.has(DEFAULT_OPTIMIZATION_MODE)) {
    return DEFAULT_OPTIMIZATION_MODE;
  }
  return normalizeOptimizationMode(fallback, FALLBACK_OPTIMIZATION_MODE);
}

export function resolveOptimizationModeFromDiagnostics(diagnostics = null) {
  const modePayload = diagnostics?.optimization_mode && typeof diagnostics.optimization_mode === "object"
    ? diagnostics.optimization_mode
    : {};
  const solverBreakdown = diagnostics?.solver_breakdown && typeof diagnostics.solver_breakdown === "object"
    ? diagnostics.solver_breakdown
    : {};
  const effectiveMode = normalizeOptimizationMode(
    solverBreakdown?.effective_optimization_mode
      || modePayload?.optimization_mode,
    DEFAULT_OPTIMIZATION_MODE,
  );
  const option = getOptimizationModeOption(effectiveMode);
  const solverTimeoutSeconds = Number.isFinite(Number(solverBreakdown?.solver_timeout_seconds_per_group))
    ? Math.round(Number(solverBreakdown.solver_timeout_seconds_per_group))
    : (Number.isFinite(Number(modePayload?.solver_timeout_seconds))
      ? Math.round(Number(modePayload.solver_timeout_seconds))
      : null);
  const requestedMode = typeof solverBreakdown?.requested_optimization_mode === "string"
    ? solverBreakdown.requested_optimization_mode.trim().toLowerCase()
    : (typeof modePayload?.requested_optimization_mode === "string"
      ? modePayload.requested_optimization_mode.trim().toLowerCase()
      : "");

  return {
    ...option,
    key: effectiveMode,
    label: typeof solverBreakdown?.effective_optimization_mode_label === "string" && solverBreakdown.effective_optimization_mode_label.trim()
      ? solverBreakdown.effective_optimization_mode_label.trim()
      : (typeof modePayload?.optimization_mode_label === "string" && modePayload.optimization_mode_label.trim()
        ? modePayload.optimization_mode_label.trim()
        : option.label),
    requestedMode: requestedMode || null,
    solverTimeoutSeconds,
    executionStrategy: typeof solverBreakdown?.optimization_execution_strategy === "string" && solverBreakdown.optimization_execution_strategy.trim()
      ? solverBreakdown.optimization_execution_strategy.trim()
      : (typeof modePayload?.execution_strategy === "string" && modePayload.execution_strategy.trim()
        ? modePayload.execution_strategy.trim()
        : option.executionStrategy),
    usedDefault: solverBreakdown?.used_default_optimization_mode === true || modePayload?.used_default_optimization_mode === true,
  };
}